/**
 * LandingWhatAgeonIs — smart-ageing system definition.
 */
import {
    Activity,
    ArrowRight,
    Microscope,
    MonitorDot,
    Sparkles,
    TrendingUp,
} from 'lucide-react';

const FEATURES = [
    {
        title: 'Advanced diagnostics',
        desc: 'Long-term health intelligence — measured, not guessed.',
        icon: Microscope,
    },
    {
        title: 'AGEON Score',
        desc: 'Baseline and health dashboard for every member journey.',
        icon: MonitorDot,
    },
    {
        title: 'Protocol-led therapies',
        desc: 'Vitality and recovery delivered as structured programmes.',
        icon: Activity,
    },
    {
        title: 'Progress tracking',
        desc: 'Measurable outcomes across every protocol and session.',
        icon: TrendingUp,
    },
    {
        title: 'Premium experience',
        desc: 'Calm, architectural member wellness from entry to renewal.',
        icon: Sparkles,
    },
];

function FeatureCard({ title, desc, icon: Icon, delay }) {
    return (
        <article
            className="group lp-lift flex h-full flex-col rounded-2xl bg-white p-6 ring-1 ring-ink-100 shadow-[0_8px_32px_-14px_rgba(15,17,21,0.08)]"
            data-aos="fade-up"
            data-aos-delay={String(delay)}
        >
            <span className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-brand-teal-50 to-white text-brand-teal-700 ring-1 ring-brand-teal/15 transition duration-300 group-hover:from-brand-teal group-hover:to-brand-teal-700 group-hover:text-white group-hover:shadow-lg group-hover:shadow-brand-teal/20">
                <Icon className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <h3 className="mt-5 font-heading text-lg font-bold leading-snug text-ink-900">{title}</h3>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-500">{desc}</p>
        </article>
    );
}

function SystemFlow() {
    return (
        <div
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-ink-900 via-[#0a2e28] to-brand-teal-700 px-6 py-8 sm:px-8 sm:py-10"
            data-aos="fade-up"
            data-aos-delay="200"
        >
            <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand-teal/20 blur-3xl" />
            <div aria-hidden className="pointer-events-none absolute -bottom-20 left-1/4 h-40 w-40 rounded-full bg-brand-gold/10 blur-3xl" />

            <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
                <div className="max-w-sm shrink-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">
                        The system
                    </p>
                    <p className="mt-2 font-heading text-xl font-bold leading-snug text-white sm:text-2xl">
                        One connected journey — not disconnected services.
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-white/55">
                        Built for repeat engagement and scalable growth.
                    </p>
                </div>

                <div className="min-w-0 flex-1 overflow-x-auto scrollbar-hide">
                    <div className="flex min-w-[40rem] items-start justify-between lg:min-w-0">
                        {FEATURES.map((step, i) => {
                            const Icon = step.icon;
                            return (
                                <div key={step.title} className="flex flex-1 items-start">
                                    <div className="flex flex-1 flex-col items-center">
                                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/10 text-brand-teal ring-1 ring-white/15 backdrop-blur-sm">
                                            <Icon className="h-4 w-4" strokeWidth={1.75} />
                                        </span>
                                        <p className="mt-2 flex min-h-[2.75rem] items-center justify-center px-1 text-center text-[10px] font-semibold leading-tight text-white/70 sm:text-[11px]">
                                            {step.title}
                                        </p>
                                    </div>
                                    {i < FEATURES.length - 1 && (
                                        <ArrowRight
                                            className="mx-0.5 mt-3.5 h-3.5 w-3.5 shrink-0 text-brand-teal/60"
                                            strokeWidth={2}
                                            aria-hidden
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function LandingWhatAgeonIs() {
    return (
        <section id="what-ageon-is" className="relative scroll-mt-24 overflow-hidden bg-ink-50 border-t border-ink-100/80">
            <div aria-hidden className="pointer-events-none absolute inset-0 landing-grain opacity-25" />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,rgba(0,176,155,0.07),transparent_55%)]"
            />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
                <div className="mx-auto max-w-3xl text-center" data-aos="fade-up">
                    <span className="lp-chip mb-6">What AGEON is</span>
                    <h2 className="font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-ink-900 sm:text-4xl lg:text-[2.75rem]">
                        Not a clinic. Not a spa.
                    </h2>
                    <p className="mt-3 font-heading text-3xl font-extrabold leading-[1.1] tracking-tight sm:text-4xl lg:text-[2.75rem]">
                        <span className="bg-gradient-to-r from-brand-teal to-brand-teal-700 bg-clip-text text-transparent">
                            A smart-ageing system.
                        </span>
                    </p>
                    <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-ink-600 sm:text-lg">
                        Diagnostics, scoring, protocols, tracking and experience — unified into one
                        measurable member journey.
                    </p>
                </div>

                <div className="mt-12 grid grid-cols-1 gap-5 sm:mt-14 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                    {FEATURES.map((feature, i) => (
                        <FeatureCard
                            key={feature.title}
                            title={feature.title}
                            desc={feature.desc}
                            icon={feature.icon}
                            delay={80 + i * 60}
                        />
                    ))}
                </div>

                <div className="mt-10 sm:mt-12">
                    <SystemFlow />
                </div>
            </div>
        </section>
    );
}
